import {MediaFile} from '@shopify/hydrogen/client';
import type {
  ExternalVideo,
  MediaEdge,
} from '@shopify/hydrogen/storefront-api-types';
import {ATTR_LOADING_EAGER, DATA_TEST_IDS} from '~/lib/const';
import clsx from 'clsx';
import {ProductGalleryAssetYoutube} from './ProductGalleryAssetYoutube.client';

export function ProductGalleryAsset({
  className,
  media,
  index,
  videoControls = 0,
  videoAutoPlay = 0,
  videoNotInteractive = true,
  isThumbnail,
  onClick,
}: {
  className?: string;
  media: MediaEdge['node'];
  index: number;
  videoControls?: 0 | 1;
  videoAutoPlay?: 0 | 1;
  videoNotInteractive?: boolean;
  isThumbnail?: boolean;
  onClick?: () => void;
}) {
  const isFirst = index === 0;
  const isFullWidth = isFirst;
  // @ts-ignore host exists in ExternalVideo in MediaEdge
  const isYoutubeVideo = media.host === 'YOUTUBE';
  let mediaProps: Record<string, any> = {
    tabIndex: 0,
    className: 'w-full h-full aspect-square fadeIn object-cover',
    size: isFullWidth
      ? '(min-width: 64em) 60vw, (min-width: 48em) 50vw, 90vw'
      : '(min-width: 64em) 30vw, (min-width: 48em) 25vw, 90vw',
  };

  const data = {
    ...media,
    image: {
      // @ts-ignore
      ...media.image,
      altText: media.alt || 'Product image',
    },
  };
  const options = {
    crop: 'center',
    scale: 2,
  } as any;

  switch (media.mediaContentType) {
    case 'IMAGE':
      mediaProps = {
        width: '100%',
        widths: [200, 300, 400],
        loading: isFirst ? 'eager' : 'lazy',
        className: 'h-full aspect-square fadeIn object-contain',
        sizes: isFullWidth
          ? '(min-width: 64em) 60vw, (min-width: 48em) 50vw, 90vw'
          : '(min-width: 64em) 30vw, (min-width: 48em) 25vw, 90vw',
      };
      break;
    case 'VIDEO':
      mediaProps = {
        width: '100%',
        style: {
          height: '100%',
          backgroundColor: 'black',
        },
        autoPlay: false,
        controls: videoControls === 1,
        muted: true,
        loop: true,
        preload: 'auto',
        playsInline: true,
      };
      break;
    case 'EXTERNAL_VIDEO':
      mediaProps = {width: '100%', height: '100%'};
      // @ts-ignore host exists in ExternalVideo in MediaEdge
      if (media.host === 'YOUTUBE') {
        options.autoplay = videoAutoPlay;
        options.loop = 1;
        options.mute = 1;
        options.controls = videoControls;
        options.rel = 0;
        options.playsinline = 1;
      }
      break;
    case 'MODEL_3D':
      mediaProps = {
        width: '100%',
        interactionPromptThreshold: '0',
        ar: true,
        loading: ATTR_LOADING_EAGER,
        disableZoom: true,
      };
      break;
  }

  if (isFirst && media.mediaContentType === 'IMAGE') {
    mediaProps.loading = ATTR_LOADING_EAGER;
  }

  const style = clsx(
    isFullWidth ? 'md:col-span-2' : 'md:col-span-1 h-full',
    'aspect-square !rounded-none bg-white md:w-full w-full h-full',
    isYoutubeVideo && 'w-full h-full overflow-hidden aspect-square',
    className,
  );

  return (
    <div
      data-testid={DATA_TEST_IDS.BUTTON('gallery-asset')}
      role="button"
      onClick={onClick}
      onKeyDown={onClick}
      tabIndex={0}
      className={style}
    >
      <div
        className={clsx('w-full h-full', {
          'pointer-events-none': isYoutubeVideo && videoNotInteractive,
        })}
      >
        {media.mediaContentType === 'EXTERNAL_VIDEO' &&
        // @ts-ignore host exists in ExternalVideo in MediaEdge
        media.host === 'YOUTUBE' ? (
          <ProductGalleryAssetYoutube
            media={media as ExternalVideo}
            isThumbnail={isThumbnail}
          />
        ) : (
          <MediaFile
            data={data}
            // @ts-ignore
            options={options}
            {...mediaProps}
          />
        )}
      </div>
    </div>
  );
}
